import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { reopenProductionMonthApi, type ProductionMonthLock } from '@/services/productionOrders'
import { formatDocumentError } from '@/utils/documentErrorMessages'

export interface MonthReopenModalProps {
  isOpen: boolean
  month: string
  lockInfo?: ProductionMonthLock | null
  onClose: () => void
  onSuccess: () => void
}

export function MonthReopenModal({
  isOpen,
  month,
  lockInfo,
  onClose,
  onSuccess,
}: MonthReopenModalProps): React.ReactElement | null {
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reason.trim()) {
      setErrorMsg('Vui lòng nhập lý do mở lại tháng sản xuất')
      return
    }

    setSubmitting(true)
    setErrorMsg(null)
    try {
      await reopenProductionMonthApi(month, reason.trim())
      onSuccess()
      onClose()
    } catch (err: any) {
      setErrorMsg(formatDocumentError(err).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-base">
            Mở lại tháng kế hoạch: <span className="font-mono text-emerald-800">{month}</span>
          </h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 font-bold"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            {lockInfo && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold">
                  Tháng {month} đã được mở lại <strong>{lockInfo.reopened_count || 0} lần</strong>.
                </p>
                {lockInfo.last_reopened_at && (
                  <p className="mt-1 text-amber-700">
                    Lần gần nhất: {new Date(lockInfo.last_reopened_at).toLocaleString('vi-VN')} bởi{' '}
                    <strong>{lockInfo.last_reopened_by || 'system'}</strong>
                  </p>
                )}
                {lockInfo.last_reopen_reason && (
                  <p className="mt-1 italic text-amber-800">
                    &quot;{lockInfo.last_reopen_reason}&quot;
                  </p>
                )}
              </div>
            )}

            {errorMsg && (
              <div className="rounded border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800">
                {errorMsg}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Lý do mở lại tháng <span className="text-rose-600">*</span>:
              </label>
              <textarea
                required
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Nhập lý do chi tiết cần điều chỉnh lại kế hoạch tháng..."
                className="w-full rounded border border-slate-300 p-2 text-sm outline-none focus:border-emerald-600"
              />
              <p className="mt-1 text-xs text-slate-500">
                Mọi mốc ngày điều chỉnh sau khi mở lại sẽ được ghi nhận độ lệch so với baseline đã duyệt.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            <Button size="sm" variant="secondary" type="button" onClick={onClose} disabled={submitting}>
              Hủy
            </Button>
            <Button size="sm" variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Đang mở lại...' : 'Xác nhận mở lại'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
